// Import React hooks
import { useCallback, useEffect, useMemo, useRef, useState } from "react";



// Import helper functions
import {
    buildPlacementEditorPayload,
    getDefaultPlacementTransform,
    normalisePositionValue,
    resolvePlacementTransform,
    rotateClockwise,
    snapRotateDegrees,
} from "../utilities/ImagePlacementEditorHelpers";



// Export the hook
export const usePercentTransformEditor = ({
    // This is the parent product sku
    productSku,

    // This is the child sku if editing a child role
    childSku,

    // This is the role code
    role,

    // This is the active flag for the role
    active,

    // This is the fit/fill mode
    fitFill,

    // This is the child role data override
    childRoleData,

    // This is the parent role data override
    parentRoleData,

    // This is the base design role data
    designRoleData,

    // This callback sends transform updates upward
    onTransformChange,
}) => {

    // This memo builds the true default transform for this role
    const defaultTransform = useMemo(() => {

        // This returns the base default transform
        return getDefaultPlacementTransform({
            role: role,
            fitFill: fitFill,
        });

    }, [
        role,
        fitFill,
    ]);



    // This memo resolves the inherited transform from design -> parent -> child
    const resolvedTransform = useMemo(() => {

        // This returns the resolved transform
        return resolvePlacementTransform({
            role: role,
            fitFill: fitFill,
            childRoleData: childRoleData,
            parentRoleData: parentRoleData,
            designRoleData: designRoleData,
            childSku: childSku,
        });

    }, [
        role,
        fitFill,
        childRoleData,
        parentRoleData,
        designRoleData,
        childSku,
    ]);



    // This memo resolves what the editor should show immediately after a reset
    const resetPreviewTransform = useMemo(() => {

        // This resolves child reset to parent -> design -> default
        if (childSku) {

            // This returns the inherited fallback without the child override
            return resolvePlacementTransform({
                role: role,
                fitFill: fitFill,
                childRoleData: null,
                parentRoleData: parentRoleData,
                designRoleData: designRoleData, 
            });

        }

        // This returns the inherited fallback without any product override
        return resolvePlacementTransform({
            role: role,
            fitFill: fitFill,
            childRoleData: null,
            parentRoleData: null,
            designRoleData: designRoleData,
            childSku: childSku,
        });

    }, [
        childSku,
        role,
        fitFill,
        parentRoleData,
        designRoleData,
    ]);



    // This stores the live editable transform shown in the editor
    const [transform, setTransform] = useState(resolvedTransform);



    // This stores the last payload emitted upward
    const lastPayloadRef = useRef(null);

    // This stores where the latest change came from
    const changeSourceRef = useRef("init");



    // This reloads the live editor state when the resolved source changes
    useEffect(() => {

        // This marks the source as an init refresh
        changeSourceRef.current = "init";

        // This sets the editor state from the resolved inherited transform
        setTransform(resolvedTransform);

    }, [resolvedTransform]);



    // This emits transform updates upward whenever the live transform changes
    useEffect(() => {

        // This exits if there is no callback
        if (!onTransformChange) {

            // This stops here
            return;

        }

        // This builds the outgoing payload
        const payload = buildPlacementEditorPayload({
            productSku: productSku,
            childSku: childSku,
            active: active,
            currentTransform: transform,
            resolvedTransform: resolvedTransform,
            changeSource: changeSourceRef.current,
        });

        // This converts the last payload to a comparable string
        const lastPayloadString = JSON.stringify(lastPayloadRef.current || {});

        // This converts the new payload to a comparable string
        const nextPayloadString = JSON.stringify(payload || {});

        // This exits if nothing meaningful changed
        if (lastPayloadString === nextPayloadString) {

            // This stops here
            return;

        }

        // This stores the newly emitted payload
        lastPayloadRef.current = payload;

        // This emits the payload upward
        onTransformChange(payload);

    }, [
        productSku,
        childSku,
        active,
        transform,
        resolvedTransform,
        onTransformChange,
    ]);





    // This updates a single transform field
    const updateTransformField = useCallback((key, value) => {

        // This marks this as a user edit
        changeSourceRef.current = "user";

        // This updates the live transform state
        setTransform((previousTransform) => ({

            // This keeps the previous transform values
            ...previousTransform,

            // This updates the requested field
            [key]: value,

            // This sets the edited value because something has been added/changed
            edited: "Y",

        }));

    }, []);

  




    // This updates centre x
    const setCentreX = useCallback((value) => {

        // This updates centre x as a number
        updateTransformField("centre_x", Number(value));

    }, [updateTransformField]);



    // This updates centre y
    const setCentreY = useCallback((value) => {

        // This updates centre y as a number
        updateTransformField("centre_y", Number(value));

    }, [updateTransformField]);



    // This updates scale x
    const setScaleX = useCallback((value) => {

        // This updates scale x as a number
        updateTransformField("scale_x", Number(value));

    }, [updateTransformField]);



    // This updates scale y
    const setScaleY = useCallback((value) => {

        // This updates scale y as a number
        updateTransformField("scale_y", Number(value));

    }, [updateTransformField]);



    // This updates scale x and y together
    const setUniformScale = useCallback((value) => {

        // This converts the incoming value to a number
        const safeScaleValue = Number(value);

        // This marks this as a user edit
        changeSourceRef.current = "user";

        // This updates both scale values together
        setTransform((previousTransform) => ({

            // This keeps the previous transform values
            ...previousTransform,

            // This updates scale x
            scale_x: safeScaleValue,

            // This updates scale y
            scale_y: safeScaleValue,

            // This marks the transform as edited
            edited: "Y",

        }));

    }, []);



    // This updates rotate
    const setRotate = useCallback((value) => {

        // This snaps the rotate value to a sensible angle
        const safeRotateValue = snapRotateDegrees(Number(value), 1);

        // This updates rotation as a number
        updateTransformField("rotation", safeRotateValue);

    }, [updateTransformField]);




    // This rotates clockwise by a fixed step
    const rotateClockwiseByStep = useCallback((step = 45) => {

        // This marks this as a user edit
        changeSourceRef.current = "user";

        // This updates the transform rotate value
        setTransform((previousTransform) => {

            // This gets the next rotation value
            const nextRotate = rotateClockwise(previousTransform?.rotation ?? 0, step);

            // This returns the updated transform
            return {

                // This keeps the previous transform values
                ...previousTransform,

                // This updates the rotate value
                rotation: nextRotate,

                // This marks the transform as edited
                edited: "Y",

            };

        });

    }, []);









    // This updates position
    const setPosition = useCallback((value) => {

        // This updates the normalised position value
        updateTransformField("position", normalisePositionValue(value));

        // Set the edited position to "Y" if it is not null (null means teh user has reset the position)
        const positionIsEdited = value !== null ? "Y" : "N";

        // This updates the edited position flag because they've done something
        updateTransformField("edited_position", positionIsEdited);

    }, [updateTransformField]);



    // This updates background colour
    const setBackgroundColour = useCallback((value) => {

        // This updates the background colour value
        updateTransformField("background_colour", value || null);

        // Set the edited background to "Y" if it is not null (null means teh user has reset the colour)
        const backgroundIsEdited = value !== null ? "Y" : "N";

        // This updates the edited background flag because they've done something
        updateTransformField("edited_background", backgroundIsEdited);

    }, [updateTransformField]);



    // This nudges the image position by small deltas
    const nudgeTransform = useCallback(({
        deltaX = 0,
        deltaY = 0,
    }) => {

        // This marks this as a user edit
        changeSourceRef.current = "user";

        // This updates the transform by nudging centre values
        setTransform((previousTransform) => ({

            // This keeps the previous transform values
            ...previousTransform,

            // This updates centre x
            centre_x: Number(previousTransform?.centre_x ?? 0.5) + Number(deltaX || 0),

            // This updates centre y
            centre_y: Number(previousTransform?.centre_y ?? 0.5) + Number(deltaY || 0),

            // This marks the transform as edited
            edited: "Y",

        }));

    }, []);



    // This applies multiple transform fields in one update
    const applyTransformPatch = useCallback((patch = {}) => {

        // This marks this as a user edit
        changeSourceRef.current = "user";

        // This updates the transform with the provided patch
        setTransform((previousTransform) => ({

            // This keeps the previous transform values
            ...previousTransform,

            // This applies the incoming patch
            ...patch,

            // This marks the transform as edited
            edited: "Y",

        }));

    }, []);



    // This resets the role override and lets inheritance take over again
    const resetTransform = useCallback(() => {

        // This marks this as a reset to inherited/default state
        changeSourceRef.current = "reset_to_default";

        // This immediately shows the inherited fallback while the parent state updates
        setTransform({

            // This keeps the resolved inherited transform fields
            ...resetPreviewTransform,

            // This always keeps the role explicit
            role: String(role || ""),

            // This restores the inherited fit/fill mode for this role
            fit_fill: String(resetPreviewTransform?.fit_fill || fitFill || "fit"),

            // This clears explicit position override
            position: null,
            
            // This clears explicit background override
            background_colour: null,

            // This clears edited flags
            edited: "N",
            edited_background: "N",
            edited_position: "N",


        });

    }, [
        role,
        fitFill,
        resetPreviewTransform,
    ]);



    // This restores the editor view back to the current inherited values without clearing saved role data
    const restoreResolvedTransform = useCallback(() => {

        // This marks this as a soft restore
        changeSourceRef.current = "reset";

        // This restores the currently resolved transform
        setTransform(resolvedTransform);

    }, [resolvedTransform]);



    // This returns the hook API
    return {

        // This exposes the true default transform
        defaultTransform: defaultTransform,

        // This exposes the resolved inherited transform
        resolvedTransform: resolvedTransform,

        // This exposes the live editor transform
        transform: transform,

        // This exposes the whole transform setter if needed externally
        setTransform: setTransform,

        // This exposes the generic field updater
        updateTransformField: updateTransformField,

        // This exposes the centre x setter
        setCentreX: setCentreX,

        // This exposes the centre y setter
        setCentreY: setCentreY,

        // This exposes the scale x setter
        setScaleX: setScaleX,

        // This exposes the scale y setter
        setScaleY: setScaleY,

        // This exposes the uniform scale setter
        setUniformScale: setUniformScale,

        // This exposes the rotate setter
        setRotate: setRotate,

        // This exposes the position setter
        setPosition: setPosition,

        // This exposes the background colour setter
        setBackgroundColour: setBackgroundColour,

        // This exposes the rotate step helper
        rotateClockwiseByStep: rotateClockwiseByStep,

        // This exposes the nudge helper
        nudgeTransform: nudgeTransform,

        // This exposes the patch helper
        applyTransformPatch: applyTransformPatch,

        // This exposes the hard reset to defaults
        resetTransform: resetTransform,

        // This exposes the soft restore to resolved values
        restoreResolvedTransform: restoreResolvedTransform,

    };

};